import moment from 'moment'
import { BehaviorSubject } from 'rxjs'
import { first, mergeMap } from 'rxjs/operators'
import { handleError, handleErrorJSON } from './RESTErrorHandler'
import { redirectIfSessionExpired } from '../../../utils/SessionUtils'
import { JSONResponse, RequestCacheOptions, ResponsePassThrough, RESTOptions, ServerError } from '../../Types/Types'
import { RESTAPIError } from './RESTAPIError'
import CLanguageHandler from '../../../Languages/LanguageHandler'
import { StatusCodes } from '../../../constants/StatusCodes'
import { LanguageErrors } from '../../../../../i18n'

const prepareFetchOptions = <T extends Nullable<object>>({
    headers,
    body,
    method,
    timeout
}: RESTOptions<T>): RequestInit => {
    const options: RequestInit = {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...headers
        },
        body: JSON.stringify(body),
        credentials: 'include'
    }

    // additional check for browser support
    if (timeout && typeof AbortSignal !== 'undefined') {
        options.signal = AbortSignal.timeout(timeout)
    }

    return options
}

// eslint-disable-next-line
type SubjectType = Definable<ResponsePassThrough<any | RESTAPIError | ServerError>>

/**
 * Request caching mechanism this is used to ensure that multiple versions of the same request dont happen at the same time.
 */
export class RequestCache {
    // How long should each cache entry last?
    private readonly ttl?: number
    // Timestamp for the previous request.
    private timestamp?: number
    // The previous response.
    private response: BehaviorSubject<SubjectType> = new BehaviorSubject<SubjectType>(undefined)
    // Language from instance.
    private language: CLanguageHandler = CLanguageHandler.getInstance()

    public constructor(options?: RequestCacheOptions) {
        this.ttl = options?.ttl
        this.language.loadBundle('LanguageErrors')
    }

    // Empty the cache and reset the timestamp.
    private emptyCache(): void {
        this.response.next(undefined)
        this.timestamp = undefined
    }

    private getResponse<T>(
        url: string,
        fetchOptions?: RequestInit,
        allowNoContent?: boolean,
        additionalAllowableNoContentCodes?: StatusCodes[],
        isString?: boolean
    ): void {
        this.timestamp = Date.now()

        fetch(url, fetchOptions)
            .then((response: Response): void => {
                if (response.status === StatusCodes.OK || response.status === StatusCodes.CREATED) {
                    if (response.headers.get('Content-Length') === '0' && allowNoContent) {
                        this.response.next({
                            status: response.status,
                            response: {}
                        })
                    } else {
                        const data: Promise<string> | Promise<JSONResponse<T>> = isString
                            ? response.text()
                            : response.json()

                        data
                            // Save the json in the cache.
                            .then((json?: JSONResponse<T> | string): void =>
                                this.response.next({
                                    status: response.status,
                                    response: json
                                })
                            )
                            .catch((): void => {
                                // If there was an error we should try again as soon as requested.
                                this.emptyCache()
                                this.response.next({
                                    status: response.status,
                                    response: new RESTAPIError(
                                        response.status,
                                        null,
                                        this.language.get(LanguageErrors.RestJsonProcessing),
                                        null
                                    )
                                })
                            })
                    }
                } else if (
                    (allowNoContent && response.status === StatusCodes.OK_NO_CONTENT) ||
                    (additionalAllowableNoContentCodes && additionalAllowableNoContentCodes.includes(response.status))
                ) {
                    this.response.next({
                        status: response.status,
                        response: {}
                    })
                } else {
                    // If there was an error we should try again as soon as requested.
                    this.emptyCache()
                    void handleError(response).then((error: RESTAPIError): void => {
                        if (!redirectIfSessionExpired(error)) {
                            this.response.next({
                                status: response.status,
                                response: error
                            })
                        }
                    })
                }
            })
            .catch((err: Error): void => {
                if (err?.name !== 'TimeoutError' && err?.name !== 'AbortError') {
                    throw err
                }

                this.response.next({
                    timedOut: true,
                    response: {}
                })
            })
    }

    /*
     * Make the fetch request with the caching possbly enabled.
     *
     * @param url - The URL to request.
     * @param fetchOptions - The standard fetch request options.
     *
     * @returns - Promise of the response.
     */
    public async fetch<T, U extends Nullable<object>>(url: string, options: RESTOptions<U>): Promise<JSONResponse<T>> {
        const fetchOptions: RequestInit = prepareFetchOptions(options)

        if (!this.timestamp || !this.ttl) {
            this.emptyCache()
            this.getResponse<T>(
                url,
                fetchOptions,
                !options.errorOnNoContent,
                options.additionalAllowableNoContentCodes,
                options.isStringResult
            )
        } else {
            // Are we still inside the caching period?
            const diff: number = moment(Date.now()).diff(moment(this.timestamp))

            if (diff > this.ttl) {
                this.emptyCache()
                this.getResponse<T>(
                    url,
                    fetchOptions,
                    !options.errorOnNoContent,
                    options.additionalAllowableNoContentCodes,
                    options.isStringResult
                )
            }
        }

        return new Promise((resolve, reject) => {
            this.response
                .pipe(first((res: SubjectType) => !!res))
                .pipe(
                    mergeMap(
                        async (res: SubjectType): Promise<ResponsePassThrough<T>> => ({
                            status: res?.status ?? 0,
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            response: await res?.response
                        })
                    )
                )
                .subscribe(
                    ({ status, response, timedOut }: ResponsePassThrough<T | RESTAPIError | ServerError>): void => {
                        // Was there an error?
                        // If so we should reject.
                        const error: RESTAPIError = response as RESTAPIError
                        const serverError: ServerError = response as ServerError

                        if (
                            options?.errorOnNoContent &&
                            (!response || JSON.stringify(response) === '{}' || status === StatusCodes.OK_NO_CONTENT)
                        ) {
                            reject(
                                new RESTAPIError(201, null, this.language.get(LanguageErrors.RestNoContentError), null)
                            )
                        } else if (error?.errorMessage || error?.errorCode) {
                            reject(response)
                        } else if (serverError?.ErrorMessage || serverError?.Message) {
                            reject(handleErrorJSON(serverError, status))
                        } else if (
                            ((status?.toString().startsWith('4') || status?.toString().startsWith('5')) &&
                                error?.JSONObject) ||
                            timedOut
                        ) {
                            reject(response)
                        } else {
                            resolve(response as JSONResponse<T>)
                        }
                    }
                )
        })
    }

    public static newInstance(options?: RequestCacheOptions): RequestCache {
        return new RequestCache(options)
    }
}
